import React, { CSSProperties, useCallback, useEffect, useRef, useState } from 'react';
import { IImage, IVideo } from '@montana/models/generated/cms.interface';
import { convertMediaUrl, isAnAssetsLink } from '@montana/helpers/media-src.helper';
import cx from 'clsx';
import { Video } from '@montana/shared/video';
import { IVideoProps } from '../video/video.component';
import { staticSettings } from '@montana/helpers/static-settings.helper';
import { transformCylindoImageSize } from '@montana/helpers/cylindo.helper';
import { SvgIcon } from '../svg-icon';
import { isBrowser, isSafari, supportsIntersectionObserver } from '@montana/helpers/browser.helper';
import { transformLDImageSize } from '@montana/helpers/london-dynamics.helper';

export enum MontanaMediaRatios {
    Landscape = (11.5 / 16),
    Square = (5 / 5),
    Portrait = (16 / 12),
    Video = (9 / 16),
    ColorHero = (1.10603291 / 1),
    StaticPDP = (363/272)
}

export interface IMediaComponentProps {
    className?: string;
    animate?: boolean;
    animateInsideLink?: boolean;
    eager?: boolean;
    fixedSize?: number;
    image?: string | IImage;
    alt?: string;
    imageText?: string;
    mediaRatio?: MontanaMediaRatios | number | false;
    keepRatio?: boolean;
    placeholder?: boolean;
    onLoad?: Function;
    overrideDefaultClassName?: string;
    sizes?: string;
    srcSetWidth?: number[];
    noSrcSet?: boolean;
    style?: CSSProperties;
    video?: IVideo;
    videoAutoplay?: boolean;
    videoLoop?: boolean;
    videoMuted?: boolean;
    videoPlayInline?: boolean;
    shouldContain?: boolean;
}

function getSrcSet(
    imageUrl: string | undefined,
    imageSizes: number[],
    ratio?: number,
) {
    const url = encodeURI(convertMediaUrl(imageUrl));
    if (url?.includes('cylindo') && !url?.includes('.svg')) {
        return `${imageSizes.map((width) => `${transformCylindoImageSize(url, width, ratio)} ${width}w`).join(', ')}`;
    } else if (url?.includes('londondynamics') && !url?.includes('.svg')) {
        return `${imageSizes.map((width) => `${transformLDImageSize(url, width, ratio)} ${width}w`).join(', ')}`;
    } else if (url?.includes(staticSettings.apiUrl) && !url?.includes('.svg')) {
        return `${imageSizes.map((width) => `${url}?mode=crop&width=${width}${ratio ? `&height=${Math.round(ratio * width)}` : ''} ${width}w`).join(', ')}`;
    } else {
        return url;
    }
}

function getSrc(
    imageUrl: string | undefined,
    imageSizes: number[],
    ratio?: number,
) {
    if (!imageUrl) {
        return undefined;
    }
    const url = encodeURI(convertMediaUrl(imageUrl));
    const width = imageSizes[imageSizes.length - 1];
    if (!isAnAssetsLink(imageUrl) && !url?.includes('.svg')) {
        if (url?.includes('cylindo')) {
            return transformCylindoImageSize(url, width, ratio);
        } else if (url?.includes('londondynamics')) {
            return transformLDImageSize(url, width, ratio);
        } else if (url?.includes(staticSettings.apiUrl) && !url?.includes('.svg')) {
            return `${url}?mode=crop&width=${width}${ratio ? `&height=${Math.round(ratio * width)}` : ''}`;
        }
    }
    return url;
}

export const mediaSrcSet = [
    24,
    32,
    100,
    280,
    414,
    540,
    640,
    828,
    1080,
    1125,
    1260,
    1520
];

export const Media: React.FC<IMediaComponentProps> = React.memo((props: IMediaComponentProps): JSX.Element => {
    const wrapperRef = useRef<HTMLDivElement | null>(null);

    const { sizes, srcSetWidth, noSrcSet, mediaRatio, keepRatio = true, animateInsideLink = true } = props;
    const blockLevelClassName = props.overrideDefaultClassName || "media";
    const shouldShowPlaceholder = !!props.placeholder;
    const shouldAnimate = typeof props.animate === 'boolean' ? props.animate : shouldShowPlaceholder;

    const imageUrl = typeof props.image === 'string'
        ? props.image
        : props.image?.url || '';
    const imageAlt = (typeof props.alt === 'string' || typeof props.image === 'string')
        ? props.alt || ''
        : props.image?.altText || '';
    const src = getSrc(imageUrl, srcSetWidth || mediaSrcSet, Number(mediaRatio));
    const srcSet = src && !noSrcSet ? getSrcSet(imageUrl, srcSetWidth?.length ? srcSetWidth : mediaSrcSet, Number(mediaRatio)) : undefined;
    const title = (typeof props.image !== 'string')
        ? props.image?.title || ''
        : undefined;

    const wrapperStyle = props.mediaRatio && keepRatio
        ? { paddingTop: `${Math.round(props.mediaRatio * 10000) / 100}%` }
        : {};

    const [mediaInView, setMediaInView] = useState(false);
    const [mediaLoaded, setMediaLoaded] = useState(false);
    const [mediaError, setMediaError] = useState(false);

    const [widthPercentage, setWidthPercentage] = useState(1);

    useEffect(() => {
        if (isBrowser) {
            if (props.eager || !supportsIntersectionObserver) {
                setMediaInView(true);
            } else {
                const observer = new IntersectionObserver((entries) => {
                    if (entries[0].isIntersecting) {
                        if (entries[0].boundingClientRect.width && entries[0].rootBounds?.width) {
                            setWidthPercentage(calculateDisplaySize(entries[0].boundingClientRect, entries[0].rootBounds));
                        }
                        setMediaInView(true);
                        observer.disconnect();
                    }
                }, {
                    rootMargin: '0px 0px 300px 0px'
                });
                if (wrapperRef && wrapperRef.current) {
                    observer.observe(wrapperRef.current);
                }

                return () => {
                    if (observer) {
                        observer.disconnect();
                    }
                };
            }

        }
    }, []);

    const onMediaReady = () => {
        setMediaLoaded(true);
        if (props.onLoad) {
            props.onLoad();
        }
    };

    const onMediaError = () => {
        setMediaError(true);
    };

    const calculateDisplaySize = (elementBounds: DOMRectReadOnly, rootBounds: DOMRectReadOnly): number => {
        const factor = elementBounds.width / rootBounds.width;
        return Math.round(factor * 10) * 10;
    };

    const imageTemplate = useCallback((): JSX.Element | null => {
        if (imageUrl) {
            return (
                <div
                    className={cx(`${blockLevelClassName}__container`, {
                        [blockLevelClassName + '__container--animate']: shouldAnimate,
                        [blockLevelClassName + '__container--placeholder']: shouldShowPlaceholder,
                        [blockLevelClassName + '__container--error']: mediaError,
                        [blockLevelClassName + '__container--loaded']: mediaLoaded
                    })}
                >
                    { mediaError &&
                        <div className="fallback-image__wrapper">
                            <SvgIcon iconName="fallback_image" className={`fallback-image ${blockLevelClassName}__broken`} />
                        </div>
                    }
                    { mediaInView
                        ? <img
                            alt={imageAlt}
                            title={title}
                            className={cx(`${blockLevelClassName}__image`, animateInsideLink ? `${blockLevelClassName}__image--hover-${widthPercentage}` : '', {
                                [`${blockLevelClassName}__image--loaded`]: mediaLoaded, [blockLevelClassName + '__image--contain']: props.shouldContain
                            })}
                            sizes={sizes}
                            // Fix Safari bug. It always loads the fallback src even though a better one exists in srcset
                            src={srcSet && isSafari ? undefined : src}
                            srcSet={srcSet}
                            onLoad={() => onMediaReady()}
                            onError={() => onMediaError()}
                        />
                        : <noscript>
                            <img alt={imageAlt} sizes={sizes} src={src} srcSet={srcSet} />
                        </noscript>
                    }
                </div>
            )
        }
        return null;
    }, [props.image, mediaInView, mediaLoaded, mediaError, shouldShowPlaceholder]);

    const videoTemplate = (): JSX.Element | null => {
        if (props?.video?.url) {
            const videoProps: Partial<IVideoProps> = {
                muted: typeof props?.videoMuted === 'boolean' ? props?.videoMuted : true,
                loop: typeof props?.videoLoop === 'boolean' ? props?.videoLoop : true,
                autoplay: typeof props?.videoAutoplay === 'boolean' ? props?.videoAutoplay : true,
                playInline: typeof props?.videoPlayInline === 'boolean' ? props?.videoPlayInline : true,
            };
            return (
                <>
                    <div
                        className={cx(`${blockLevelClassName}__container`, {
                            [blockLevelClassName + '__container--animate']: shouldAnimate,
                            [blockLevelClassName + '__container--placeholder']: shouldShowPlaceholder,
                            [blockLevelClassName + '__container--loaded']: mediaLoaded
                        })}
                    >
                        <div
                            className={cx(`${blockLevelClassName}__video`, `${blockLevelClassName}__video--width-${widthPercentage}`, {
                                [`${blockLevelClassName}__video--animate-in-link`]: animateInsideLink,
                                [`${blockLevelClassName}__video--loaded`]: mediaLoaded
                            })}
                        >
                            {
                                <Video
                                    style={{ scale: 1.01 }} // small scale up workaround to remove black bars in youtube videos
                                    thumbnail={props.video.coverImage}
                                    url={props.video.url}
                                    title={props.video.title || props.video.altText}
                                    onReady={() => onMediaReady()}
                                    // eslint-disable-next-line react/prop-types
                                    {...videoProps}
                                />
                            }
                        </div>
                    </div>
                </>
            )
        }
        return null;
    };

    return (
        <figure
            ref={wrapperRef}
            className={cx(blockLevelClassName, props.className, {
                [blockLevelClassName + '--manual']: props.mediaRatio === false
            })}
            style={props.style}
        >
            <div className={`${blockLevelClassName}__wrapper`} style={wrapperStyle}>
                {
                    props.video
                        ? videoTemplate()
                        : imageTemplate()
                }
            </div>
            {
                props.imageText
                    ? <figcaption className={`${blockLevelClassName}__caption`}>{props.imageText}</figcaption>
                    : null
            }
        </figure>
    );
});
